import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api";
import { useTier, useAuth } from "@/context/AuthContext";
import { isPremiumType } from "@/lib/premiumLocks";

const STORAGE_KEY = "vincent_active_collection_id";

const CollectionContext = createContext({
  collections: [],
  activeId: null,
  activeCollection: null,
  refresh: async () => {},
  removeById: () => {},
  setActive: () => {},
  loading: true,
});

export const CollectionProvider = ({ children }) => {
  const [rawCollections, setRawCollections] = useState([]);
  const [activeId, setActiveIdState] = useState(() => {
    try { return window.localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const { canPremiumUI } = useTier();
  const { user } = useAuth();
  const userId = user?.id || null;

  const refresh = useCallback(async () => {
    // Multi-tenant isolation in the SPA layer: without an authenticated
    // user, we NEVER show anyone's collections. Complements the backend
    // owner_id filter on every /api/collections* endpoint.
    if (!userId) {
      setRawCollections([]);
      setLoading(false);
      return;
    }
    try {
      const { data } = await apiClient.get("/collections");
      // Reconcile: the backend response is the ONLY source of truth for
      // which collection ids may exist in the SPA. `setRawCollections`
      // performs a full replacement (not a merge), so any orphan tile
      // that was hanging around in context — from a session that
      // pre-dated the owner_id scope, from a stale optimistic write, or
      // from a legacy premium-lock cache — is discarded on every
      // refresh. Also proactively prunes the client-side premium-lock
      // set so no lock badge lingers for ids the backend no longer
      // reports.
      setRawCollections(Array.isArray(data) ? data : []);
      try {
        if (typeof window !== "undefined" && Array.isArray(data)) {
          const authoritative = new Set(data.map((c) => c.id));
          const raw = window.localStorage.getItem("cdi:premium_locked_collections");
          if (raw) {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr)) {
              const pruned = arr.filter((x) => typeof x === "string" && authoritative.has(x));
              if (pruned.length !== arr.length) {
                window.localStorage.setItem("cdi:premium_locked_collections", JSON.stringify(pruned));
              }
            }
          }
        }
      } catch { /* private mode / corrupt JSON — best effort */ }
    } catch (e) {
      console.error("collections fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Optimistic removal used by the Collections page after a successful
  // DELETE. Strips the id from `rawCollections` synchronously so every
  // consumer of `useCollections()` (Collections page tiles, dropdowns
  // in New Item / New Category, Settings pickers) re-renders without
  // the deleted tile immediately — before the follow-up `refresh()`
  // round-trip resolves. The follow-up refresh remains authoritative.
  const removeById = useCallback((id) => {
    if (!id) return;
    setRawCollections((prev) => prev.filter((c) => c.id !== id));
  }, []);

  // Refetch (and clear stale data) whenever the authenticated user
  // changes — login, logout, or account switch. Prevents another
  // account's collection tiles from lingering after a session change
  // in the same browser tab. On logout, also drops the persisted
  // active-collection id so the next login starts clean.
  useEffect(() => {
    setRawCollections([]);
    if (!userId) {
      setActiveIdState(null);
      try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    }
    refresh();
  }, [userId, refresh]);

  // CDI Master Rule — Trial + Basic users must NEVER see any Premium
  // collection (existing OR locked by a downgrade), anywhere in the app.
  // Filtering here means every downstream consumer of useCollections()
  // — Items page, form dropdowns, sidebar picker, aggregations —
  // automatically respects the gate. Admin bypasses because canPremiumUI
  // folds in is_admin.
  const collections = useMemo(() => {
    if (canPremiumUI) return rawCollections;
    return rawCollections.filter((c) => {
      if (isPremiumType(c.type_id)) return false;
      return true;
    });
  }, [rawCollections, canPremiumUI]);

  // Resolve active id whenever the filtered list changes. If the previous
  // active id has been filtered out (e.g. downgrade), fall back to the
  // first visible collection.
  useEffect(() => {
    if (!collections.length) return;
    setActiveIdState((prev) => {
      if (prev && collections.find((c) => c.id === prev)) return prev;
      return collections[0]?.id || null;
    });
  }, [collections]);

  useEffect(() => {
    try {
      if (activeId) window.localStorage.setItem(STORAGE_KEY, activeId);
    } catch { /* ignore */ }
  }, [activeId]);

  const setActive = (id) => setActiveIdState(id);

  const activeCollection = collections.find((c) => c.id === activeId) || null;

  return (
    <CollectionContext.Provider value={{ collections, activeId, activeCollection, refresh, removeById, setActive, loading }}>
      {children}
    </CollectionContext.Provider>
  );
};

export const useCollections = () => useContext(CollectionContext);

// Convenience hook for formatting amounts using the active collection's currency
export const useFormatMoney = () => {
  const { activeCollection } = useContext(CollectionContext);
  return (v) => {
    const n = Number(v || 0);
    const sym = (activeCollection && activeCollection.currency_symbol) || "$";
    const code = (activeCollection && activeCollection.currency) || "NZD";
    return `${sym}${n.toFixed(2)} ${code}`;
  };
};
